import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  checkUserExistApi,
  validateBusinessEmailApi,
  loginApi,
  checkWebaddressAvailApi,
  checkCompanyNameApi,
  createUserApi,
  resendEmailApi,
  sendOtpApi,
  verifyOtpApi,
  resetPassApi,
  updateUserPreferenceApi,
  inviteUserApi,
  bulkInviteUsersApi,
  resendInviteApi,
  revokeInviteApi,
  updateUserApi,
  updateSelfApi,
  deleteUserApi,
  logoutUser,
  approveUserApi,
} from "./users.api";

export const useCheckUserExist = () => {
  return useMutation({
    mutationFn: (email: string) => checkUserExistApi(email),
  });
};

export const useValidateBusinessEmail = () => {
  return useMutation({
    mutationFn: (email: string) => validateBusinessEmailApi(email),
  });
};

export const useCheckWebaddressAvail = () => {
  return useMutation({
    mutationFn: (webaddress: string) => checkWebaddressAvailApi(webaddress),
  });
};

export const useCheckCompanyNameAvail = () => {
  return useMutation({
    mutationFn: (name: string) => checkCompanyNameApi(name),
  });
};

export const useLogin = () => {
  return useMutation({
    mutationFn: (userData: any) => loginApi(userData),
  });
};

export const useCreateUserAccount = () => {
  return useMutation({
    mutationFn: (userData: object) => createUserApi(userData),
  });
};

export const useResendEmail = () => {
  return useMutation({
    mutationFn: (email: string) => resendEmailApi(email),
  });
};

export const useSendOtp = () => {
  return useMutation({
    mutationFn: (email: string) => sendOtpApi(email),
  });
};

export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      verifyOtpApi(email, otp),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassApi,
  });
};

export const useUpdateUserPreference = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: (payload: any) => updateUserPreferenceApi(payload),
    onSuccess,
  });
};
export const useInviteUser = () => {
  return useMutation({
    mutationFn: (userData: object) => inviteUserApi(userData),
  });
};

export const useBulkInviteUsers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      invites: Array<{
        email: string;
        firstName?: string;
        role?: string;
        projectMapping?: Array<{ projectId: number; roleId: number }>;
      }>;
    }) => bulkInviteUsersApi(payload),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["userList"] });
      queryClient.refetchQueries({ queryKey: ["allUsers"] });
      queryClient.refetchQueries({ queryKey: ["billing", "usage"] });
    },
  });
};

export const useResendInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => resendInviteApi(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userList"] });
    },
  });
};

export const useRevokeInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { invitationId?: number; email?: string }) =>
      revokeInviteApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userList"] });
    },
  });
};

// export const useUpdateUser = () => {
//   return useMutation({
//     mutationFn: (userData: object) => updateUserApi(userData),
//   });
// };
type UpdateUserInput = {
  id: number;
  payload: any;
};
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: UpdateUserInput) =>
      updateUserApi(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    },
  });
};

/** Self-service profile update — no USERS:WRITE permission needed. */
export const useUpdateSelf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      firstName?: string;
      lastName?: string;
      phonenumber?: number | null;
    }) => updateSelfApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    },
  });
};

export const useLogoutUser = () => {
  return useMutation({
    mutationFn: () => logoutUser(),
  });
};

export const useApproveUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => approveUserApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userList"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteUserApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userList"] });
    },
  });
};
